import { Link } from "react-router-dom";
import { useSeo, SITE_ORIGIN, SITE_NAME } from "@/hooks/useSeo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import Reveal from "@/components/feature/Reveal";
import SectionHeading from "@/components/feature/SectionHeading";
import { siteMeta } from "@/mocks/siteMeta";

const positioning = [
  {
    icon: "ri-layout-grid-2-line",
    title: "做聚合，而不是做列表",
    content:
      "同样的内容，普通列表按时间往下铺，我们的专区按方向重新归拢，一个页面对应一整块内容，翻找的次数明显更少。",
  },
  {
    icon: "ri-user-heart-line",
    title: "面向想快速跟上的读者",
    content:
      "不要求你从头追到尾，也不要求你记住大量背景。打开任何一个专区，读一段摘要就能判断要不要往下看。",
  },
  {
    icon: "ri-shield-check-line",
    title: "明确不做什么",
    content:
      "不做搬运式堆砌，不做诱导点击的标题，不涉及任何具体个人的私人信息。这三条是内容整理时不可让步的底线。",
  },
];

export default function AboutPage() {
  useSeo({
    title: "关于我们 - 爆料91专区内容聚合门户介绍",
    description:
      "了解爆料91的品牌定位、内容组织方式与编辑原则。我们做的是把栏目、频道、板块内容归拢成合集的聚合门户，服务想快速跟上话题的读者，不涉及个人隐私内容。",
    keywords: "关于爆料91,爆料91介绍,爆料91内容原则,内容聚合门户",
    path: "/about",
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于爆料91",
        url: `${SITE_ORIGIN}/about`,
        inLanguage: "zh-CN",
        description: "爆料91内容聚合门户的品牌介绍、定位与编辑原则。",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: SITE_NAME,
        url: `${SITE_ORIGIN}/`,
        email: siteMeta.email,
        foundingDate: siteMeta.foundedYear,
        description: siteMeta.description,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_ORIGIN}/` },
          { "@type": "ListItem", position: 2, name: "关于我们", item: `${SITE_ORIGIN}/about` },
        ],
      },
    ],
  });

  return (
    <>
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 pt-6 pb-10 md:pb-14">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <div className="mt-6 max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs font-label tracking-[0.2em] uppercase text-primary-700">
              <span className="w-5 h-px bg-primary-500"></span>
              About
            </span>
            <h1 className="mt-3 font-heading font-black text-3xl md:text-4xl text-foreground-950">
              关于爆料91
            </h1>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
              {siteMeta.description}
            </p>
          </div>

          <dl className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-5 max-w-3xl">
            {siteMeta.stats.map((stat) => (
              <div key={stat.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
                <dd className="font-heading font-bold text-2xl text-foreground-950">
                  {stat.value}
                  <span className="text-sm font-medium text-foreground-500 ml-1">{stat.unit}</span>
                </dd>
                <dt className="mt-1 text-xs text-foreground-600">{stat.label}</dt>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="w-full px-4 md:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-6">
              <SectionHeading
                eyebrow="Our Story"
                title="把散落的内容，收进一个入口"
                description="爆料91从内容归类的实际问题出发：同一部作品、同一个话题，讨论往往分散在好几个地方，读者要来回跳。"
              />
              <div className="mt-6 flex flex-col gap-4 text-sm md:text-base leading-relaxed text-foreground-600">
                <p>
                  我们做的事说起来很简单：把方向相同的内容先归到一个专区，
                  再把专区里有价值的条目按主题编成合集。读者不用记住内容分散在哪几个位置，
                  只要选对专区，剩下的交给整理的顺序。
                </p>
                <p>
                  从 {siteMeta.foundedYear} 年开始，我们陆续把热门方向拆成独立的专区，
                  每个专区下面再按栏目和板块细分。划分标准都写在页面上，
                  你看得见自己在哪一层，也随时能退回去换一个方向。
                </p>
                <p>
                  编辑团队由 {siteMeta.editTeam} 负责，内容以「能不能帮读者快速判断」为唯一标准，
                  做不到这一点，宁可不收进来。
                </p>
              </div>
            </div>
            <div className="lg:col-span-6">
              <Reveal>
                <figure className="w-full aspect-[4/3] overflow-hidden rounded-lg bg-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20editorial%20photograph%20of%20a%20tidy%20content%20editing%20desk%20with%20open%20notebooks%2C%20color%20swatch%20cards%20and%20a%20cup%20of%20tea%2C%20soft%20natural%20window%20light%2C%20calm%20organized%20workspace%2C%20cream%20and%20amber%20tones%2C%20magazine%20feature%20photography&width=1200&height=900&seq=about-brand-91&orientation=landscape"
                    alt="爆料91编辑团队的整理工作台"
                    title="爆料91 - 内容整理工作台"
                    className="w-full h-full object-cover object-top"
                  />
                </figure>
                <figcaption className="mt-3 text-xs text-foreground-500">
                  内容整理以「读者能不能快速判断」为取舍标准
                </figcaption>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 border-y border-background-200">
        <div className="w-full px-4 md:px-8 py-12 md:py-16">
          <SectionHeading
            eyebrow="Positioning"
            title="网站定位"
            description="先说清楚我们是什么、服务谁、又刻意不做什么，避免读者产生错误预期。"
          />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {positioning.map((item, index) => (
              <Reveal key={item.title} delay={index * 80}>
                <div className="h-full rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-4 font-heading font-bold text-lg text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-600">{item.content}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="w-full px-4 md:px-8 py-12 md:py-16">
          <SectionHeading
            eyebrow="Principles"
            title="内容原则"
            description="这四条在每次整理时都会被拿出来对照，任何一条不满足，内容就不会出现在专区里。"
          />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {siteMeta.principles.map((item, index) => (
              <Reveal key={item.title} delay={index * 70}>
                <div className="h-full flex gap-4 rounded-lg border border-background-200 bg-background-100 p-6">
                  <span className="font-label text-2xl text-accent-700 shrink-0 w-10">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-heading font-bold text-lg text-foreground-950">
                      {item.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                      {item.content}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 border-y border-background-200">
        <div className="w-full px-4 md:px-8 py-12 md:py-16">
          <SectionHeading
            eyebrow="Feedback"
            title="读者怎么说"
            description="下面这些反馈来自长期使用专区的读者，我们按方向整理后保留原文感受。"
          />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {siteMeta.testimonials.map((item, index) => (
              <Reveal key={item.name} delay={index * 80}>
                <blockquote className="h-full flex flex-col rounded-lg border border-background-200 bg-background-50 p-6">
                  <i className="ri-double-quotes-l text-2xl text-accent-500"></i>
                  <p className="mt-3 flex-1 text-sm md:text-[15px] leading-relaxed text-foreground-700">
                    {item.content}
                  </p>
                  <footer className="mt-5 pt-4 border-t border-background-200 flex items-center gap-3">
                    <span className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-900 font-medium text-sm">
                      {item.name.slice(0, 1)}
                    </span>
                    <span className="flex flex-col">
                      <cite className="not-italic text-sm font-medium text-foreground-900">
                        {item.name}
                      </cite>
                      <span className="text-xs text-foreground-500">{item.role}</span>
                    </span>
                  </footer>
                </blockquote>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="w-full px-4 md:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-5">
              <SectionHeading
                eyebrow="Contact"
                title="联系我们"
                description="内容纠错、线索建议、栏目共建，都可以直接发邮件给对应邮箱，我们会安排栏目编辑跟进。"
              />
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <span className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <i className="ri-mail-send-line text-xl"></i>
                </span>
                <h3 className="mt-4 font-heading font-bold text-base text-foreground-950">
                  内容纠错与反馈
                </h3>
                <a
                  href={`mailto:${siteMeta.email}`}
                  className="mt-2 block text-sm text-primary-700 break-all cursor-pointer hover:underline"
                >
                  {siteMeta.email}
                </a>
                <p className="mt-3 text-xs text-foreground-500">
                  反馈内容问题时，请附上专区名称与具体位置，便于我们定位。
                </p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <span className="w-11 h-11 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                  <i className="ri-team-line text-xl"></i>
                </span>
                <h3 className="mt-4 font-heading font-bold text-base text-foreground-950">
                  栏目共建与合作
                </h3>
                <a
                  href={`mailto:${siteMeta.cooperationEmail}`}
                  className="mt-2 block text-sm text-primary-700 break-all cursor-pointer hover:underline"
                >
                  {siteMeta.cooperationEmail}
                </a>
                <p className="mt-3 text-xs text-foreground-500">
                  想新增一个专区方向，说明内容范围和更新频率即可。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-accent-50 border-t border-accent-200">
        <div className="w-full px-4 md:px-8 py-12 md:py-14">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="max-w-2xl">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
                先从专区总览逛起
              </h2>
              <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed">
                全部栏目、频道、板块入口都在那里。选一个方向点进去，合集已经替你排好顺序。
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/zones"
                className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md bg-primary-600 text-background-50 text-sm whitespace-nowrap cursor-pointer hover:bg-primary-700 transition-colors"
              >
                <i className="ri-layout-grid-fill"></i>
                专区总览
              </Link>
              <Link
                to="/faq"
                className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-md border border-foreground-300 text-sm text-foreground-900 whitespace-nowrap cursor-pointer hover:border-foreground-900 transition-colors"
              >
                <i className="ri-question-line"></i>
                常见问题
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}